import { Stack } from "@mui/material";
import { MooneLitePopup } from "Components/Dashboard/MooneLitePopup";
import { CustomerUserAccountDetail } from "Components/UserAccount/Customer/CustomerUserAccountDetail";
import { EmployeeUserAccountDetail } from "Components/UserAccount/Employee/EmployeeUserAccountDetail";
import { HunterUserAccountDetail } from "Components/UserAccount/Hunter/HunterUserAccountDetail";
import { MerchantUserAccountDetail } from "Components/UserAccount/Merchant/MerchantUserAccountDetail";
import type { AccountDetailProps } from "Components/UserAccount/types";
import { useIsUserInMooneLite } from "Hooks/useIsUserInMooneLite";
import { AccessRoleCodes, type AccessRoleCode } from "Models/AccessRoleCodes";
import type { FC } from "react";
import type { PartyDto } from "Infrastructure/Api/Api";
import { ROLES_ALLOWED_TO_DEACTIVATE_USER } from "Components/UserAccount/constants";
import type { UserVerificationUpdate } from "Components/UserAccount/Shared/VerificationForm/schema";

const USER_ROLE_TO_ACCOUNT_DETAIL: Record<
  AccessRoleCode,
  FC<AccountDetailProps>
> = {
  [AccessRoleCodes.Merchant]: MerchantUserAccountDetail,
  [AccessRoleCodes.Hunter]: HunterUserAccountDetail,
  [AccessRoleCodes.Customer]: CustomerUserAccountDetail,
  [AccessRoleCodes.Admin]: CustomerUserAccountDetail,
  [AccessRoleCodes.AdminLite]: CustomerUserAccountDetail,
  [AccessRoleCodes.Aml]: CustomerUserAccountDetail,
  [AccessRoleCodes.Employee]: EmployeeUserAccountDetail,
  [AccessRoleCodes.CustomerOnboarding]: CustomerUserAccountDetail,
  [AccessRoleCodes.HunterPlus]: HunterUserAccountDetail,
  [AccessRoleCodes.Disponent]: CustomerUserAccountDetail,
  [AccessRoleCodes.Child]: CustomerUserAccountDetail,
};

type Props = {
  userRole: AccessRoleCode;
  party: PartyDto;
  verification: UserVerificationUpdate;
};

export const UserAccountDetail = ({ party, verification, userRole }: Props) => {
  const { isLiteTier, isDocExpired, isLiteBecauseOfBank } =
    useIsUserInMooneLite();

  // Doc-expired users must reach the detail to access DocumentsDisplay and re-upload, and so must
  // a customer whose identity KYC is done and who is Lite only for the missing bank — the data and
  // documents shown here are theirs. Only users who never finished onboarding get bounced to the CTA.
  if (isLiteTier && !isDocExpired && !isLiteBecauseOfBank) {
    return <MooneLitePopup />;
  }

  const canDeactivateAccount =
    ROLES_ALLOWED_TO_DEACTIVATE_USER.includes(userRole);
  const DetailComponent = USER_ROLE_TO_ACCOUNT_DETAIL[userRole];

  return (
    <Stack gap={4}>
      <DetailComponent
        party={party}
        verification={verification}
        canDeactivateAccount={canDeactivateAccount}
      />
    </Stack>
  );
};
